/**
 * A/B/C Test Composable
 *
 * Provides access to A/B/C test variant information throughout the application.
 * Supports both Varnish-initiated and application-initiated experiments.
 *
 * The server middleware (server/middleware/ab-test.ts) reads headers from Varnish
 * and stores the test information in the event context. The server plugin
 * (app/plugins/ab-test.server.ts) initializes the state on every request.
 * This composable provides reactive access to that state.
 *
 * Usage (Varnish-initiated):
 * ```typescript
 * const { experiment, group, variant, hasActiveTest, isVariant } = useAbTest()
 *
 * // Check if user is in a specific group
 * if (isVariant('B')) {
 *   // Show variant B
 * }
 * ```
 *
 * Usage (Application-initiated):
 * ```typescript
 * const { activateExperiment, isVariant } = useAbTest()
 *
 * // Activate experiment with auto-normalized weights
 * activateExperiment('exp100', { A: 7, B: 2, C: 1 }) // 70% A, 20% B, 10% C
 *
 * if (isVariant('B')) {
 *   // Show variant B content
 * }
 * ```
 *
 * @see Issue #209577 for specification
 */

// Import server utility for activation (only used server-side)
import { activateAbTest } from '../../server/utils/ab-test-activate'

export interface AbTestData {
  experiment: string | null
  group: string | null
  variant: string | null
}

// Re-export VariantWeights from the assignment utility
export type { VariantWeights } from '../../server/utils/ab-test-assignment'

export function useAbTest() {
  const config = useRuntimeConfig()
  const event = useRequestEvent()

  // Mark experiment as used on server-side whenever composable is accessed
  // This triggers the response header plugin to set x-ab-variant and Vary headers
  if (import.meta.server && event) {
    event.context.abTestUsed = true
  }

  // Access the state initialized by the server plugin
  // This state is shared between server and client (hydration)
  const state = useState<AbTestData>('ab-test', () => ({
    experiment: null,
    group: null,
    variant: null,
  }))

  // Computed values for reactive access
  const experiment = computed(() => state.value.experiment)
  const group = computed(() => state.value.group)
  const variant = computed(() => state.value.variant)
  const hasActiveTest = computed(() => !!state.value.variant)

  /**
   * Check if the user is in a specific test group
   *
   * @param targetGroup - The group to check ('A', 'B', or 'C')
   * @returns true if the user is in the specified group
   *
   * @example
   * if (isVariant('B')) {
   *   // Show variant B content
   * }
   */
  const isVariant = (targetGroup: string): boolean => {
    return state.value.group === targetGroup
  }

  /**
   * Activate an application-initiated A/B test experiment
   *
   * This function allows you to programmatically start an A/B test from within
   * your pages or components. Weights are automatically normalized, so they
   * don't need to sum to 100.
   *
   * Important notes:
   * - Server-side only (no-op on client, returns false)
   * - Varnish-initiated tests take precedence (returns false if Varnish active)
   * - First call wins (subsequent calls return false)
   * - Invalid weights return false silently
   * - Response headers are set via the ab-test-headers plugin
   * - Varnish caching provides variant consistency across requests
   *
   * @param experimentName - Unique experiment identifier (e.g., 'exp100')
   * @param weights - Variant weights (auto-normalized). Examples:
   *   - { A: 7, B: 2, C: 1 } → 70% A, 20% B, 10% C
   *   - { A: 1 } → 100% A
   *   - { A: 100, B: 50 } → 66.7% A, 33.3% B
   * @returns boolean - true if experiment activated successfully, false otherwise
   *
   * @example
   * const { activateExperiment, isVariant } = useAbTest()
   *
   * // Activate experiment with 70% A, 20% B, 10% C
   * if (activateExperiment('exp100', { A: 7, B: 2, C: 1 })) {
   *   // Experiment activated successfully
   *   if (isVariant('B')) {
   *     // Show variant B content
   *   }
   * }
   */
  const activateExperiment = (
    experimentName: string,
    weights: { A: number, B?: number, C?: number },
  ): boolean => {
    // Client-side: no-op
    if (import.meta.client || !event) {
      return false
    }

    // Call server utility to handle activation logic
    const result = activateAbTest(event, experimentName, weights)

    // Update state with result
    if (result.success) {
      state.value = {
        experiment: result.experiment,
        group: result.group,
        variant: result.variant,
      }
    }

    return result.success
  }

  // Debug logging in development mode (client-side only to avoid duplication)
  if (config.public.isDev && hasActiveTest.value && import.meta.client) {
    console.log('[useAbTest - Client]', {
      experiment: experiment.value,
      group: group.value,
      variant: variant.value,
    })
  }

  const trackExperiment = () => {
    const gtm = useGtm()
    onMounted(() => {
      if (hasActiveTest.value) {
        gtm?.push({
          event: 'experiment_view',
          experiment_id: experiment.value,
          experiment_group: group.value,
          experiment_variant: variant.value,
        })
      }
    })
  }

  return {
    /** The experiment name (e.g., "exp84") or null if no test is active */
    experiment,
    /** The assigned group ("A", "B", "C") or null if no test is active */
    group,
    /** The combined variant identifier (e.g., "exp84_A") or null if no test is active */
    variant,
    /** Whether an A/B test is currently active */
    hasActiveTest,
    /** Check if the user is in a specific test group */
    isVariant,
    /** Activate an application-initiated experiment (server-side only) */
    activateExperiment,
    /** Track an experiment view (client-side only) */
    trackExperiment,
  }
}
